import dayjs from "dayjs";
import { Calendar, Check } from "lucide-react";
import { ProcessedAssignment } from "./type";
import { Badge } from "@/components/ui/badge";
import {
  ViewMode,
  JobAssignment,
  ScheduledJob
} from "@/hooks/services/pps/handlers/schedule/job-schedule-types";

// CONSTANTS
export const SCHEDULE_CONSTANTS = {
  GRID_START_HOUR: 0,
  GRID_END_HOUR: 24,
  TOTAL_COLUMNS: 24, // 24 hour columns covering full day
  WORK_HOURS: 9, // 17 - 8 = 9 work hours (kept for weekly detail grid)
  WORK_START_HOUR: 8,
  WORK_END_HOUR: 17,
  MONTHLY_COL_WIDTH: 80, // 5rem = 80px (w-20)
  DAILY_LANE_HEIGHT: 80,
  WEEKLY_LANE_HEIGHT: 68, // Increased to accommodate 4 lines of text
  LEFT_PANEL_WIDTH: 220, // px — matches Figma job card panel width
  /** Minimum row height in px to ensure sidebar card content is never clipped.
   * Derived from: py-4×2 (32) + 3 label/value sections (~96) + space-y-3 gaps (~36) + badge row (28) = ~192px */
  SIDEBAR_ROW_MIN_HEIGHT: 215,
  /** Vertical padding (px) applied inside each Gantt row cell — bars are inset by this amount top and bottom. */
  ROW_PAD: 8
} as const;

// WEEKEND / HOLIDAY BACKGROUND TOKENS (WOR-2187)
// Reuses the /plant-3/technicians/schedule reference tokens so the calendar matches.
export const SCHEDULE_BG_TOKENS = {
  weekend: "aero:bg-gray-200",
  holiday: "aero:bg-yellow-200",
  today: "aero:bg-primary/5"
} as const;

/** Saturday (6) or Sunday (0). */
export const isWeekendDay = (day: dayjs.ConfigType): boolean => {
  const d = dayjs(day).day();
  return d === 0 || d === 6;
};

/**
 * Single day-column background class with precedence today > holiday > weekend > none.
 * `holidays` maps "YYYY-MM-DD" -> holiday name (see useHolidays).
 */
export const getDayBgClass = (
  day: dayjs.ConfigType,
  holidays?: ReadonlyMap<string, string>
): string => {
  if (dayjs(day).isToday()) return SCHEDULE_BG_TOKENS.today;
  if (holidays?.has(dayjs(day).format("YYYY-MM-DD")))
    return SCHEDULE_BG_TOKENS.holiday;
  if (isWeekendDay(day)) return SCHEDULE_BG_TOKENS.weekend;
  return "";
};

/** Holiday name for a given day, if any. */
export const getHolidayName = (
  day: dayjs.ConfigType,
  holidays?: ReadonlyMap<string, string>
): string | undefined => holidays?.get(dayjs(day).format("YYYY-MM-DD"));

/**
 * Shift hour boundaries (24h), matching backend ScheduleBarColorHelper:
 * AM 06:00–14:00, PM 14:00–22:00, OT 22:00–06:00 (OT never flagged). Used to
 * position the time-based availability overlay in the Day (Timeline) view (WOR-2187).
 */
export const SHIFT_HOURS = {
  amStart: 6,
  amEnd: 14,
  pmStart: 14,
  pmEnd: 22
} as const;

// ZOOM CONSTANTS
// zoomLevel: 20..100 (step 10).
// Both column width AND lane height scale linearly with zoom.
// Base values are defined at ZOOM_MIN (20%). At 100% they are 5× larger.
export const ZOOM_MIN = 20;
export const ZOOM_MAX = 100;
export const ZOOM_STEP = 10;
/** Base column widths at ZOOM_MIN (20%). Scale linearly up to ZOOM_MAX (100%). */
export const ZOOM_BASE_COL_WIDTH: Record<ViewMode, number> = {
  daily: 103, // px per hour column — FIXED (Figma: 1028px ÷ 10 cols = 102.8px), zoom does NOT scale columns in day view
  weekly: 140, // px per day column — FIXED at Figma 100%-scale width (140px), zoom does NOT scale columns in week view
  monthly: 147 // px per day column at 20% zoom — matches Figma 193-111582 (146.857px)
};

/**
 * Returns column width in px.
 * - Daily view:   FIXED at 103px regardless of zoom (zoom only affects lane/row height)
 * - Weekly view:  FIXED at 140px regardless of zoom (Figma 100% scale = 140px)
 * - Monthly view: scaled by zoom level
 */
export const getColWidth = (
  viewMode: ViewMode,
  zoomLevel: number = 100
): number => {
  if (viewMode === "daily") return ZOOM_BASE_COL_WIDTH.daily;
  if (viewMode === "weekly") return ZOOM_BASE_COL_WIDTH.weekly;
  return Math.round((ZOOM_BASE_COL_WIDTH[viewMode] * zoomLevel) / ZOOM_MIN);
};

/**
 * Returns lane height in px scaled by zoom for weekly/monthly view.
 * At 20% → 68px  (barHeight = 68 - ROW_PAD(8) = 60px  — Figma 236-111299)
 * At 100% → 168px (barHeight = 168 - ROW_PAD(8) = 160px — Figma 236-114621)
 */
export const getLaneHeight = (zoomLevel: number): number => {
  const MIN_H = 68; // px at ZOOM_MIN
  const MAX_H = 168; // px at ZOOM_MAX
  return Math.round(
    MIN_H + ((MAX_H - MIN_H) * (zoomLevel - ZOOM_MIN)) / (ZOOM_MAX - ZOOM_MIN)
  );
};

/**
 * Returns lane height in px for the TECHNICIAN weekly view.
 * At 20% → 32px  (barHeight = 32 - ROW_PAD(8) = 24px  — Figma 206-133454: height 24)
 * At 100% → 116px (barHeight = 116 - ROW_PAD(8) = 108px — Figma 206-134899: height 108)
 */
export const getTechLaneHeight = (zoomLevel: number): number => {
  const MIN_H = 32; // px at ZOOM_MIN
  const MAX_H = 116; // px at ZOOM_MAX
  return Math.round(
    MIN_H + ((MAX_H - MIN_H) * (zoomLevel - ZOOM_MIN)) / (ZOOM_MAX - ZOOM_MIN)
  );
};

/**
 * Returns event bar height in px for the MONTHLY view, scaled by zoom.
 * At 20% → 22px  (compact single-line bar — Figma 193-112481)
 * At 100% → 140px (full card bar — Figma 239-120358)
 */
export const getMonthlyBarH = (zoomLevel: number): number => {
  const MIN_H = 22;
  const MAX_H = 140;
  return Math.round(
    MIN_H + ((MAX_H - MIN_H) * (zoomLevel - ZOOM_MIN)) / (ZOOM_MAX - ZOOM_MIN)
  );
};

/**
 * Returns week-row cell height in px for the MONTHLY view, scaled by zoom.
 * At 20% → 188px, at 100% → 600px.
 */
export const getMonthlyCellH = (zoomLevel: number): number => {
  const MIN_H = 188;
  const MAX_H = 600;
  return Math.round(
    MIN_H + ((MAX_H - MIN_H) * (zoomLevel - ZOOM_MIN)) / (ZOOM_MAX - ZOOM_MIN)
  );
};

/**
 * Returns event bar height in px for the TECHNICIAN MONTHLY view, scaled by zoom.
 * At 20% → 24px  (inline bar — Figma 206-137438: height 24)
 * At 100% → 84px  (full bar — Figma 206-141891: height 84)
 */
export const getTechMonthlyBarH = (zoomLevel: number): number => {
  const MIN_H = 24;
  const MAX_H = 84;
  return Math.round(
    MIN_H + ((MAX_H - MIN_H) * (zoomLevel - ZOOM_MIN)) / (ZOOM_MAX - ZOOM_MIN)
  );
};

/**
 * Returns week-row cell height in px for the TECHNICIAN MONTHLY view, scaled by zoom.
 * Bars are smaller (24–84px) so cells need less height than job monthly view.
 * At 20% → 100px, at 100% → 300px.
 */
export const getTechMonthlyCellH = (zoomLevel: number): number => {
  const MIN_H = 100;
  const MAX_H = 300;
  return Math.round(
    MIN_H + ((MAX_H - MIN_H) * (zoomLevel - ZOOM_MIN)) / (ZOOM_MAX - ZOOM_MIN)
  );
};

/**
 * Returns lane height for the DAILY view, scaled by zoom.
 * Matches the Figma reference bar height of 56px at ZOOM_MIN (20%).
 * Scales linearly to 140px at ZOOM_MAX (100%).
 */
export const getDailyLaneHeight = (zoomLevel: number): number => {
  const MIN_H = 56; // px at ZOOM_MIN — matches Figma event bar height
  const MAX_H = 140; // px at ZOOM_MAX
  return Math.round(
    MIN_H + ((MAX_H - MIN_H) * (zoomLevel - ZOOM_MIN)) / (ZOOM_MAX - ZOOM_MIN)
  );
};

// PRIORITY COLOR MAPPING

export const PRIORITY_COLORS: Record<number, string> = {
  1: "aero:bg-green-500 aero:border-green-600",
  2: "aero:bg-yellow-500 aero:border-yellow-600",
  3: "aero:bg-red-500 aero:border-red-600"
};

export const getPriorityColor = (priority: number): string => {
  return PRIORITY_COLORS[priority] || "aero:bg-blue-500 aero:border-blue-600";
};

// ─── Job bar color tokens ────────────────────────────────────────────────────
// When the business logic for bar coloring is defined, replace the constant
// below with the result of getJobBarColor(yourCondition) and the UI will
// update everywhere automatically.
//
// Palette:
//   green  → bg-green-100 / border-green-600 / text-green-900   (current default)
//   yellow → bg-yellow-50 / border-yellow-500 / text-yellow-900
//   red    → bg-red-50    / border-red-700    / text-red-900

export type JobBarVariant = "green" | "yellow" | "red";

const JOB_BAR_VARIANTS: Record<JobBarVariant, { color: string; text: string }> =
  {
    green: {
      color: "aero:bg-green-100 aero:border-green-600",
      text: "aero:text-green-900"
    },
    yellow: {
      color: "aero:bg-yellow-50 aero:border-yellow-500",
      text: "aero:text-yellow-900"
    },
    red: {
      color: "aero:bg-red-50 aero:border-red-700",
      text: "aero:text-red-900"
    }
  };

const JOB_BAR_ACCENT_VARIANTS: Record<JobBarVariant, string> = {
  green: "aero:bg-green-600",
  yellow: "aero:bg-yellow-600",
  red: "aero:bg-red-600"
};

const isJobBarVariant = (value: unknown): value is JobBarVariant =>
  value === "green" || value === "yellow" || value === "red";

const resolveJobBarVariantForTime = (
  startDate: string | null | undefined,
  barVariant?: JobBarVariant | null
): JobBarVariant => {
  if (isJobBarVariant(barVariant)) {
    return barVariant;
  }

  return isOtHour(startDate) ? "red" : "green";
};

/**
 * Returns the bar color + text classes for the given variant.
 * Swap out `"green"` for your variant when the business logic is ready.
 *
 * @example
 *   const { color, text } = getJobBarColor("yellow");
 */
export const getJobBarColor = (
  variant: JobBarVariant = "green"
): { color: string; text: string } => JOB_BAR_VARIANTS[variant];

/**
 * Returns bar color + text classes driven by a priority ID.
 * Mirrors the legend:
 *   1 → green  (normal / on-track)
 *   2 → yellow (at-risk)
 *   3 → red    (critical / overdue)
 *   other → green (default until logic is defined)
 *
 * Hardcode the ID at each call site today; swap for a dynamic value
 * (e.g. assignment.priority) when the business rules are finalised.
 *
 * @example
 *   const { color, text } = getJobBarColorByPriority(1); // green
 *   const { color, text } = getJobBarColorByPriority(2); // yellow
 *   const { color, text } = getJobBarColorByPriority(3); // red
 */
export const PRIORITY_TO_BAR_VARIANT: Record<number, JobBarVariant> = {
  1: "green",
  2: "yellow",
  3: "red"
};

export const getJobBarColorByPriority = (
  priorityId: number
): { color: string; text: string } =>
  JOB_BAR_VARIANTS[PRIORITY_TO_BAR_VARIANT[priorityId] ?? "green"];

/**
 * OT shift window: 22:00–06:00.
 * Returns true if the given date/time string falls within OT hours.
 */
export const isOtHour = (startDate: string | null | undefined): boolean => {
  if (!startDate) return false;
  const hour = dayjs(startDate).hour();
  return hour >= 22 || hour < 6;
};

/**
 * Returns bar color, text, and left-accent classes for a job assignment bar.
 * Uses backend-provided variant when available, otherwise falls back to OT logic.
 */
export const getJobBarColorForTime = (
  startDate: string | null | undefined,
  barVariant?: JobBarVariant | null
): { color: string; text: string; accent: string } => {
  const variant = resolveJobBarVariantForTime(startDate, barVariant);
  const { color, text } = JOB_BAR_VARIANTS[variant];
  const accent = JOB_BAR_ACCENT_VARIANTS[variant];

  return { color, text, accent };
};

// Default variant — change this one value when the design decision lands.
const DEFAULT_JOB_BAR_VARIANT: JobBarVariant = "green";

/** @deprecated Use getJobBarColor() instead when variant logic is defined. */
export const JOB_BAR_COLOR = JOB_BAR_VARIANTS[DEFAULT_JOB_BAR_VARIANT].color;
/** @deprecated Use getJobBarColor() instead when variant logic is defined. */
export const JOB_BAR_TEXT = JOB_BAR_VARIANTS[DEFAULT_JOB_BAR_VARIANT].text;

// ASSIGNMENT PROCESSING
export const processAssignmentWithColor = <T extends JobAssignment>(
  assignment: T,
  baseJobId?: string
): ProcessedAssignment => {
  const priority = assignment.priority || 1;
  return {
    ...assignment,
    priorityColor: getPriorityColor(priority),
    baseJobId
  };
};

// TIME OFFSET CALCULATION
export const getTimeOffsetInDay = (dateTime: dayjs.Dayjs): number => {
  const { GRID_START_HOUR, GRID_END_HOUR } = SCHEDULE_CONSTANTS;
  const totalHours = GRID_END_HOUR - GRID_START_HOUR;
  const hour = dateTime.hour();
  const minute = dateTime.minute();
  const timeInHours = hour + minute / 60;

  if (timeInHours <= GRID_START_HOUR) return 0;
  if (timeInHours >= GRID_END_HOUR) return 1;
  return (timeInHours - GRID_START_HOUR) / totalHours;
};

// DAILY VIEW - ASSIGNMENT POSITION
// Returns px-based positions so columns scale with zoomLevel.
export const getAssignmentPosition = (
  assignment: ProcessedAssignment,
  currentDay: Date | null,
  zoomLevel: number = 100
) => {
  if (!currentDay) return null;

  const startDateStr = assignment.startDate;
  const eddDateStr = assignment.eddDate;

  if (!startDateStr) {
    return null;
  }

  const startDate = dayjs(startDateStr);
  const endDate = eddDateStr ? dayjs(eddDateStr) : startDate;
  const viewDate = dayjs(currentDay);

  const isStartBeforeView = startDate.isBefore(viewDate, "day");
  const isEndAfterView = endDate.isAfter(viewDate, "day");

  if (endDate.isBefore(viewDate, "day") || startDate.isAfter(viewDate, "day")) {
    return null;
  }

  const { GRID_START_HOUR, GRID_END_HOUR, TOTAL_COLUMNS } = SCHEDULE_CONSTANTS;
  const colPx = getColWidth("daily", zoomLevel); // px per hour column

  let startTimeInHours: number;
  let endTimeInHours: number;

  if (isStartBeforeView) {
    startTimeInHours = GRID_START_HOUR;
  } else {
    const startHour = startDate.hour();
    const startMinute = startDate.minute();
    startTimeInHours = startHour + startMinute / 60;
  }

  if (isEndAfterView) {
    endTimeInHours = GRID_END_HOUR;
  } else {
    const endHour = endDate.hour();
    const endMinute = endDate.minute();
    endTimeInHours = endHour + endMinute / 60;
  }

  const startOffset = Math.max(0, startTimeInHours - GRID_START_HOUR);
  const endOffset = Math.min(TOTAL_COLUMNS, endTimeInHours - GRID_START_HOUR);

  const leftPx = startOffset * colPx;
  const widthPx = Math.max((endOffset - startOffset) * colPx, colPx * 0.5);

  return {
    leftPx,
    widthPx,
    // Keep % variants for callers that still need them
    position: (leftPx / (TOTAL_COLUMNS * colPx)) * 100,
    width: (widthPx / (TOTAL_COLUMNS * colPx)) * 100,
    isMultiDay: isStartBeforeView || isEndAfterView,
    isStartClipped: isStartBeforeView,
    isEndClipped: isEndAfterView
  };
};

/**
 * Normalize an end date string to end-of-day when it's a date-only value
 * (no time component). This ensures that two assignments on the same day
 * (e.g. "2026-05-28") are treated as overlapping — without this,
 * dayjs("2026-05-28") === midnight, so isBefore(midnight) is false and
 * overlap is never detected, causing all same-day tasks to land on lane 0.
 */
const normalizeEndDate = (dateStr: string) => {
  const hasTime = dateStr.includes("T") || dateStr.includes(" ");
  return hasTime ? dayjs(dateStr) : dayjs(dateStr).endOf("day");
};

export const calculateLanes = (assignments: ProcessedAssignment[]) => {
  const assignmentsWithLanes = assignments.map((a) => ({ ...a, lane: 0 }));

  assignmentsWithLanes.sort((a, b) => {
    const aStart = a.startDate ? dayjs(a.startDate).valueOf() : 0;
    const bStart = b.startDate ? dayjs(b.startDate).valueOf() : 0;
    return aStart - bStart;
  });

  assignmentsWithLanes.forEach((assignment, index) => {
    if (!assignment.startDate) return;

    const assignmentStart = dayjs(assignment.startDate);
    const assignmentEnd = assignment.eddDate
      ? normalizeEndDate(assignment.eddDate)
      : normalizeEndDate(assignment.startDate);

    let lane = 0;
    let foundLane = false;

    while (!foundLane) {
      const hasOverlap = assignmentsWithLanes.some((other, otherIndex) => {
        if (otherIndex >= index || other.lane !== lane) return false;
        if (!other.startDate) return false;

        const otherStart = dayjs(other.startDate);
        const otherEnd = other.eddDate
          ? normalizeEndDate(other.eddDate)
          : normalizeEndDate(other.startDate);

        return (
          assignmentStart.isBefore(otherEnd) &&
          assignmentEnd.isAfter(otherStart)
        );
      });

      if (!hasOverlap) {
        assignment.lane = lane;
        foundLane = true;
      } else {
        lane++;
      }
    }
  });

  const maxLane = Math.max(...assignmentsWithLanes.map((a) => a.lane), 0);
  return { assignments: assignmentsWithLanes, laneCount: maxLane + 1 };
};

export const OverviewJobSchedule = (
  job: ScheduledJob,
  assignments: ProcessedAssignment[],
  jobId: string,
  onClick?: () => void,
  isSelected?: boolean
) => {
  const totalDuration = assignments.reduce(
    (sum, a) => sum + (a.duration || 0),
    0
  );
  const totalTasks = assignments.length;
  const firstAssignment = assignments[0];
  const eddDate = job?.overallEddDate ?? firstAssignment?.eddDate;
  const firstPartAssignment = assignments.find(
    (assignment) =>
      Boolean(assignment.partNo && assignment.partNo.trim()) ||
      Boolean(assignment.partDescription && assignment.partDescription.trim())
  );
  const partNo =
    (job.partNo && job.partNo.trim()) || firstPartAssignment?.partNo || "";
  const partDescription =
    (job.partDescription && job.partDescription.trim()) ||
    firstPartAssignment?.partDescription ||
    "";
  const customerName =
    (job.customerName && job.customerName.trim()) ||
    assignments.find((assignment) =>
      Boolean(assignment.customerName && assignment.customerName.trim())
    )?.customerName ||
    "";

  return (
    <div
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={[
        "group aero:flex-shrink-0 aero:py-4 aero:h-full aero:pl-4 aero:pr-4 aero:border-r aero:border-neutral-300 aero:bg-white aero:transition-colors",
        "aero:flex aero:flex-col aero:gap-[9px]",
        onClick ? "aero:cursor-pointer" : "",
        isSelected
          ? "aero:!bg-brand-primary-50"
          : "aero:bg-white aero:hover:bg-muted/40"
      ]
        .filter(Boolean)
        .join(" ")}
      style={{ width: `${SCHEDULE_CONSTANTS.LEFT_PANEL_WIDTH}px` }}
    >
      {/* Checkbox — top of card, in normal flow; appears on hover or always when selected */}
      {onClick && (
        <div
          className={[
            "aero:w-4 aero:h-4 aero:rounded-sm aero:border aero:flex aero:items-center aero:justify-center aero:transition-all aero:flex-shrink-0",
            isSelected
              ? "aero:border-brand-primary-600 aero:bg-brand-primary-600 aero:opacity-100"
              : "aero:border-neutral-400 aero:bg-white aero:opacity-0 group-hover:aero:opacity-100"
          ].join(" ")}
        >
          {isSelected && (
            <Check className="aero:h-3 aero:w-3 aero:text-white" />
          )}
        </div>
      )}
      {/* Job Order */}
      <div>
        <p className="aero:text-[8px] aero:font-normal aero:leading-[12px] aero:text-neutral-500">
          Job Order
        </p>
        <p
          className="aero:font-bold aero:text-[14px] aero:leading-[20px] aero:truncate"
          style={{ color: "#1A6B52" }}
          title={jobId}
        >
          {jobId}
        </p>
      </div>

      {/* Part Number & Description */}
      <div>
        <p className="aero:text-[8px] aero:font-normal aero:leading-[12px] aero:text-neutral-500">
          Part Number &amp; Description
        </p>
        <p
          className="aero:text-[12px] aero:font-normal aero:leading-[16px] aero:text-neutral-900 aero:truncate"
          title={partNo || undefined}
        >
          {partNo || "—"}
        </p>
        <p
          className="aero:text-[10px] aero:font-normal aero:leading-[16px] aero:text-neutral-500 aero:truncate"
          title={partDescription || undefined}
        >
          {partDescription || "—"}
        </p>
      </div>

      {/* Customer Name */}
      <div>
        <p className="aero:text-[8px] aero:font-normal aero:leading-[12px] aero:text-neutral-500">
          Customer Name
        </p>
        <p
          className="aero:text-[12px] aero:font-normal aero:leading-[16px] aero:text-neutral-900 aero:truncate"
          title={customerName || undefined}
        >
          {customerName || "—"}
        </p>
      </div>

      {/* Summary chips */}
      <div className="aero:flex aero:items-center aero:gap-1.5 aero:flex-wrap">
        <Badge
          variant="secondary"
          className="aero:text-[10px] aero:font-normal aero:leading-[16px] aero:h-5 aero:px-1.5 aero:rounded-full aero:text-[#1A1A1A]"
        >
          {totalTasks} {totalTasks === 1 ? "task" : "tasks"}
        </Badge>
        <Badge
          variant="outline"
          className="aero:text-[10px] aero:font-normal aero:leading-[16px] aero:h-5 aero:px-1.5 aero:rounded-full aero:text-[#1A1A1A]"
        >
          {totalDuration}h
        </Badge>
        {eddDate && (
          <Badge
            variant="outline"
            className="aero:text-[10px] aero:font-normal aero:leading-[16px] aero:h-5 aero:px-1.5 aero:rounded-full aero:bg-amber-50 aero:border-amber-300 aero:text-[#92400E] aero:gap-0.5"
          >
            <Calendar className="aero:h-2.5 aero:w-2.5 aero:flex-shrink-0" />
            {dayjs(eddDate).format("MMM D")}
          </Badge>
        )}
      </div>
    </div>
  );
};

// WEEKLY/MONTHLY VIEW - LANE CALCULATION (full date-range overlap)
export const calculateLanesForSpanningView = (
  assignments: ProcessedAssignment[]
): {
  assignments: (ProcessedAssignment & { lane: number })[];
  maxLane: number;
} => {
  const assignmentsWithLanes = assignments.map((a) => ({ ...a, lane: 0 }));

  // Sort by start date
  assignmentsWithLanes.sort((a, b) => {
    const aStart = a.startDate ? dayjs(a.startDate).valueOf() : 0;
    const bStart = b.startDate ? dayjs(b.startDate).valueOf() : 0;
    return aStart - bStart;
  });

  // Two assignments overlap if their day ranges intersect
  const rangesOverlap = (
    a: ProcessedAssignment,
    b: ProcessedAssignment
  ): boolean => {
    if (!a.startDate || !b.startDate) return false;
    const aStart = dayjs(a.startDate).startOf("day");
    const aEnd = a.eddDate ? dayjs(a.eddDate).startOf("day") : aStart;
    const bStart = dayjs(b.startDate).startOf("day");
    const bEnd = b.eddDate ? dayjs(b.eddDate).startOf("day") : bStart;
    return !aStart.isAfter(bEnd, "day") && !bStart.isAfter(aEnd, "day");
  };

  // Assign lanes: find lowest lane with no overlapping assignment already placed there
  assignmentsWithLanes.forEach((assignment, index) => {
    let lane = 0;
    let foundLane = false;
    while (!foundLane) {
      const hasOverlap = assignmentsWithLanes.some((other, otherIndex) => {
        if (otherIndex >= index || other.lane !== lane) return false;
        return rangesOverlap(assignment, other);
      });
      if (!hasOverlap) {
        assignment.lane = lane;
        foundLane = true;
      } else {
        lane++;
      }
    }
  });

  const maxLane = Math.max(...assignmentsWithLanes.map((a) => a.lane), 0);
  return { assignments: assignmentsWithLanes, maxLane };
};

// WEEKLY/MONTHLY VIEW - SPANNING BAR POSITION
export interface SpanningBarPosition {
  leftPosition: string;
  barWidthStyle: string;
  isStartClipped: boolean;
  isEndClipped: boolean;
  isVisible: boolean;
}

export const getSpanningBarPosition = (
  assignment: ProcessedAssignment,
  timelineDays: Date[],
  viewMode: ViewMode,
  zoomLevel: number = 100
): SpanningBarPosition | null => {
  if (!assignment.startDate) return null;
  if (timelineDays.length === 0) return null;

  const totalDays = timelineDays.length;
  const colPx = getColWidth(viewMode, zoomLevel);

  const assignmentStartFull = dayjs(assignment.startDate);
  const assignmentEndFull = assignment.eddDate
    ? dayjs(assignment.eddDate)
    : assignmentStartFull;
  const assignmentStartDay = assignmentStartFull.startOf("day");
  const assignmentEndDay = assignmentEndFull.startOf("day");
  const timelineStart = dayjs(timelineDays[0]).startOf("day");
  const timelineEnd = dayjs(timelineDays[totalDays - 1]).startOf("day");

  // Not visible if completely outside the timeline
  if (
    assignmentEndDay.isBefore(timelineStart, "day") ||
    assignmentStartDay.isAfter(timelineEnd, "day")
  ) {
    return {
      leftPosition: "0",
      barWidthStyle: "0",
      isStartClipped: false,
      isEndClipped: false,
      isVisible: false
    };
  }

  const isStartClipped = assignmentStartDay.isBefore(timelineStart, "day");
  const isEndClipped = assignmentEndDay.isAfter(timelineEnd, "day");

  // Clamp to timeline boundaries
  const effectiveStartDay = isStartClipped ? timelineStart : assignmentStartDay;
  const effectiveEndDay = isEndClipped ? timelineEnd : assignmentEndDay;

  const startCol = timelineDays.findIndex((d) =>
    dayjs(d).startOf("day").isSame(effectiveStartDay, "day")
  );
  const endCol = timelineDays.findIndex((d) =>
    dayjs(d).startOf("day").isSame(effectiveEndDay, "day")
  );

  if (startCol === -1) return null;
  const resolvedEndCol = endCol === -1 ? totalDays - 1 : endCol;

  // Use percentage-based positions for weekly/monthly so columns can grow to fill
  // the container width without breaking bar alignment.
  if (viewMode !== "daily") {
    const startPct = (startCol / totalDays) * 100;
    const widthPct = ((resolvedEndCol + 1 - startCol) / totalDays) * 100;
    return {
      leftPosition: `calc(${startPct.toFixed(4)}% + 1px)`,
      barWidthStyle: `calc(${widthPct.toFixed(4)}% - 2px)`,
      isStartClipped,
      isEndClipped,
      isVisible: true
    };
  }

  // Daily view: pixel-based (columns are fixed-width in daily view)
  const startPixel = startCol * colPx;
  const endPixel = (resolvedEndCol + 1) * colPx; // +1 = fill to end of the end day
  const barWidth = endPixel - startPixel;

  return {
    leftPosition: `${startPixel + 1}px`,
    barWidthStyle: `${Math.max(barWidth - 2, 20)}px`,
    isStartClipped,
    isEndClipped,
    isVisible: true
  };
};

// GRID WIDTH CALCULATOR
/** Returns total grid width in px. For weekly/monthly views, all columns are fixed-px. */
export const getGridWidth = (
  viewMode: ViewMode,
  totalDays: number,
  zoomLevel: number = 100
): number => {
  return totalDays * getColWidth(viewMode, zoomLevel);
};

export const getContainerMinWidth = (
  viewMode: ViewMode,
  totalDays: number,
  zoomLevel: number = 100
): string => {
  return `${192 + getGridWidth(viewMode, totalDays, zoomLevel)}px`;
};

// ── Detail-view overlap grouping ─────────────────────────────────────────────
/** Item with a pre-computed vertical pixel position (for the 100%-zoom detail grid). */
export interface TimePositionedItem<T> {
  topPx: number;
  heightPx: number;
  data: T;
}

/**
 * Groups items into clusters of time-overlapping bars (for the weekly detail/100%-zoom view).
 * Two items overlap when their [topPx, topPx+heightPx) ranges intersect.
 * Sorted by topPx; uses greedy interval merging so contiguous overlaps form one cluster.
 */
export function groupByTimeOverlap<T>(
  items: TimePositionedItem<T>[]
): TimePositionedItem<T>[][] {
  if (items.length === 0) return [];
  const sorted = [...items].sort((a, b) => a.topPx - b.topPx);
  const clusters: TimePositionedItem<T>[][] = [];
  const clusterMaxEnd: number[] = [];

  for (const item of sorted) {
    let placed = false;
    for (let i = 0; i < clusters.length; i++) {
      if (item.topPx < clusterMaxEnd[i]) {
        clusters[i].push(item);
        clusterMaxEnd[i] = Math.max(
          clusterMaxEnd[i],
          item.topPx + item.heightPx
        );
        placed = true;
        break;
      }
    }
    if (!placed) {
      clusters.push([item]);
      clusterMaxEnd.push(item.topPx + item.heightPx);
    }
  }
  return clusters;
}
